import { VolumeUnit } from 'shared/src/model/common'
import { LanguageCode } from 'shared/src/util/language'
import { isValue } from 'shared/src/util/typeGuard'
import { getDefaultNumberFormatOptions, getTranslationStringForUnit } from 'shared/src/util/unit/unit'

export class Volume {
  private _liter: number

  constructor(value: number, unit: VolumeUnit) {
    this._liter = value * this.getUnitFactor(unit)
  }

  static liter(liter: number): Volume {
    return new Volume(liter, VolumeUnit.LITER)
  }

  static ounceFluidUS(ounce: number): Volume {
    return new Volume(ounce, VolumeUnit.OUNCE_FLUID_US)
  }

  static zero(): Volume {
    return Volume.liter(0)
  }

  valueOf() {
    return this._liter
  }

  private getUnitFactor(unit: VolumeUnit): number {
    switch (unit) {
      case VolumeUnit.LITER:
        return 1
      case VolumeUnit.OUNCE_FLUID_US:
        return 0.0295735
      case VolumeUnit.PINT_LIQUID_US:
        return 0.473176
      case VolumeUnit.QUART_LIQUID_US:
        return 0.946353
      case VolumeUnit.GALLON_LIQUID_US:
        return 3.78541
      default:
        throw new Error('Unknown unit')
    }
  }

  to(targetUnit: VolumeUnit): number {
    return this._liter / this.getUnitFactor(targetUnit)
  }

  toRoundedDecimals(targetUnit: VolumeUnit): number {
    const targetValue = this.to(targetUnit)
    const decimals = this.getDecimals(targetUnit)
    if (targetUnit === VolumeUnit.LITER) {
      return Math.round(targetValue)
    }
    if (isValue(decimals)) {
      return Math.round(targetValue * decimals.length) / decimals.length
    }
    return targetValue
  }

  getOnlyInteger(targetUnit: VolumeUnit): number {
    const targetValue = this.to(targetUnit)
    return Math.trunc(targetValue)
  }

  getOnlyRoundedInteger(targetUnit: VolumeUnit): number {
    const targetValue = this.toRoundedDecimals(targetUnit)
    return Math.trunc(targetValue)
  }

  getOnlyFraction(targetUnit: VolumeUnit): number {
    const targetValue = this.to(targetUnit)
    return targetValue - Math.floor(targetValue)
  }

  getOnlyRoundedFraction(targetUnit: VolumeUnit): number {
    const targetValue = this.toRoundedDecimals(targetUnit)
    return targetValue - Math.floor(targetValue)
  }

  private getDecimals(unit: VolumeUnit): number[] | null {
    switch (unit) {
      case VolumeUnit.GALLON_LIQUID_US:
        return [0, 5]
      default:
        return null
    }
  }

  // Getters
  get liter(): number {
    return this._liter / this.getUnitFactor(VolumeUnit.LITER)
  }

  get roundedLiter(): number {
    return Math.round(this.liter)
  }

  get ounceFluidUS(): number {
    return this._liter / this.getUnitFactor(VolumeUnit.OUNCE_FLUID_US)
  }

  get pintLiquidUS(): number {
    return this._liter / this.getUnitFactor(VolumeUnit.PINT_LIQUID_US)
  }

  get quartLiquidUS(): number {
    return this._liter / this.getUnitFactor(VolumeUnit.QUART_LIQUID_US)
  }

  get gallonLiquidUS(): number {
    return this._liter / this.getUnitFactor(VolumeUnit.GALLON_LIQUID_US)
  }

  toString(langCode = LanguageCode.ENGLISH): string {
    const options = getDefaultNumberFormatOptions(VolumeUnit.LITER)
    return Intl.NumberFormat(langCode, options).format(this._liter)
  }

  toStringWithUnit(volumeUnit = VolumeUnit.LITER, langCode: LanguageCode): string {
    const value = this.toRoundedDecimals(volumeUnit)
    const options = getDefaultNumberFormatOptions(volumeUnit)
    return Intl.NumberFormat(langCode, options).format(value)
  }

  toStringWithUnitSuffixed(volumeUnit = VolumeUnit.LITER, langCode: LanguageCode): string {
    const value = this.toRoundedDecimals(volumeUnit)
    const options = getDefaultNumberFormatOptions(volumeUnit)
    return `${Intl.NumberFormat(langCode, options).format(value)} ${getTranslationStringForUnit(volumeUnit)}`
  }

  // Operators
  add(other: Volume): Volume {
    return Volume.liter(this._liter + other._liter)
  }

  subtract(other: Volume): Volume {
    return Volume.liter(this._liter - other._liter)
  }

  multiply(factor: number): Volume {
    return Volume.liter(this._liter * factor)
  }

  divide(divisor: number): Volume {
    if (divisor === 0) {
      throw new Error('Cannot divide by zero')
    }
    return Volume.liter(this._liter / divisor)
  }

  equals(other: Volume): boolean {
    return this._liter === other._liter
  }

  greaterThan(other: Volume): boolean {
    return this._liter > other._liter
  }

  lessThan(other: Volume): boolean {
    return this._liter < other._liter
  }

  greaterThanOrEqual(other: Volume): boolean {
    return this._liter >= other._liter
  }

  lessThanOrEqual(other: Volume): boolean {
    return this._liter <= other._liter
  }

  negate(): Volume {
    return Volume.liter(-this._liter)
  }
}
